// components/CommissionsTable.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility,
  TrendingUp,
  TrendingDown,
  Info,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, enGB, enAU, enCA, de, es, fr, it, nl } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';
import ExportOrdersModal from './ExportOrdersModal';
import { Download } from '@mui/icons-material';
import { Button } from '@mui/material';

const CommissionsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Restore state from navigation if available
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(location.state?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(location.state?.rowsPerPage ?? 25);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState(location.state?.orderBy ?? 'order_date');
  const [order, setOrder] = useState(location.state?.order ?? 'desc');
  const [filters, setFilters] = useState(() => location.state?.filters ?? {
    type: 'all',
    status: 'approved', // Default to showing approved commissions
    partner_retailer: '',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end_date: new Date(),
    include_declined: false, // Only include approved by default
  });
  const [partners, setPartners] = useState([]);
  const [tabValue, setTabValue] = useState(location.state?.tabValue ?? 0);
  const [exportOpen, setExportOpen] = useState(false);

  // Get user's locale for date formatting
  const getUserLocale = () => {
    const browserLocale = navigator.language || navigator.languages?.[0] || 'en-GB';
    console.log('🌍 Browser locale detected:', browserLocale);
    console.log('🌍 All browser languages:', navigator.languages);

    // Default to DD/MM/YYYY format (enGB) for international users
    // Only use MM/DD/YYYY (enUS) if explicitly set to en-US
    const localeMap = {
      // All other English-speaking countries use DD/MM/YYYY (default)
      'en': enGB,
      'en-GB': enGB,
      'en-AU': enAU,
      'en-CA': enCA,
      'en-NZ': enGB, // New Zealand uses DD/MM/YYYY like UK
      'en-IE': enGB, // Ireland uses DD/MM/YYYY like UK
      'en-ZA': enGB, // South Africa uses DD/MM/YYYY like UK
      // Other languages
      'de': de,
      'de-DE': de,
      'es': es,
      'es-ES': es,
      'fr': fr,
      'fr-FR': fr,
      'it': it,
      'it-IT': it,
      'nl': nl,
      'nl-NL': nl,
    };

    const selectedLocale = localeMap[browserLocale] || localeMap[browserLocale.split('-')[0]] || enGB;
    console.log('🌍 Selected date-fns locale:', selectedLocale === enGB ? 'enGB' : selectedLocale === enAU ? 'enAU' : selectedLocale === nl ? 'nl' : selectedLocale === enUS ? 'enUS' : 'other');

    return selectedLocale; // Default to UK format (DD/MM/YYYY) for international users
  };

  const statusColors = {
    'auto_approved': 'success',
    'manually_approved': 'success',
    'pending': 'info',
    'declined': 'error',
    'approved': 'success', // Consolidated status
  };

  const statusLabels = {
    'auto_approved': 'Approved',
    'manually_approved': 'Approved', 
    'pending': 'Pending',
    'declined': 'Declined',
    'approved': 'Approved', // Consolidated status
  };
  
  // Available filter options (only valid statuses)
  const availableStatuses = {
    'approved': 'Approved',
    'pending': 'Pending',
    'declined': 'Declined'
  };


  const loadPartners = useCallback(async () => {
    try {
      // Always include all partners regardless of commission status for the dropdown
      const response = await dashboardAPI.getPartners(true);
      if (response.success) {
        setPartners(response.data);
      }
    } catch (err) {
      console.error('Failed to load partners:', err);
    }
  }, []);

  // Load partners once on mount
  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  // Create a memoized fetch function to prevent unnecessary re-renders
  const fetchCommissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const typeMapping = ['all', 'owed', 'earned'];
      const currentType = typeMapping[tabValue];
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sort: orderBy,
        order: order,
        type: currentType,
        status: filters.status,
        partner_retailer: filters.partner_retailer,
        start_date: filters.start_date?.toISOString(),
        end_date: filters.end_date?.toISOString(),
        include_declined: filters.include_declined,
      };

      // Filter out undefined values to avoid sending them to the API
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key];
        }
      });

      console.log('📊 Loading commissions with params:', params);
      console.log('📊 Current filters being used:', {
        type: currentType,
        status: filters.status,
        partner_retailer: filters.partner_retailer,
        start_date: filters.start_date?.toISOString(),
        end_date: filters.end_date?.toISOString(),
        include_declined: filters.include_declined
      });
      const response = await dashboardAPI.getCommissions(params);
      console.log('📊 Commission response:', response);
      
      if (response.success) {
        setCommissions(response.data.items || []);
        setTotalCount(response.data.pagination?.total_items || 0);
        console.log(`📊 Loaded ${(response.data.items || []).length} commissions`);
      } else {
        console.error('❌ Failed to load commissions:', response);
        setError(response.error || 'Failed to load commissions');
        setCommissions([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('❌ Commission loading error:', err);
      setError(err.message || 'An error occurred');
      setCommissions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, orderBy, order, tabValue, 
      filters.status, filters.partner_retailer, filters.start_date, filters.end_date, filters.include_declined]);

  // Load commissions on mount and when dependencies change
  useEffect(() => {
    console.log('🔄 Loading commissions (mount or dependency change)');
    console.log('Current filters:', filters);
    console.log('Current tabValue:', tabValue);
    console.log('Current page:', page);
    console.log('Current orderBy:', orderBy);
    fetchCommissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [fetchCommissions]);


  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };
      
      // When status is changed, adjust include_declined accordingly
      if (field === 'status') {
        if (value === '' || value === 'all') {
          // When "All Statuses" is selected, include declined records
          newFilters.include_declined = true;
        } else {
          // When a specific status is selected, include only that status
          newFilters.include_declined = false;
        }
      }
      
      return newFilters;
    });
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCommissionAmount = (commission) => {
    return commission.commission_type === 'owed' 
      ? commission.match_retailer_commission_paid 
      : commission.reference_retailer_commission_amount;
  };

  // The rate that produced the amount shown above it. The receiving retailer pays the full
  // bid (10%); the sending retailer earns the bid minus HandOff's share of it (8%). Showing
  // "10% bid" under an 8% amount is what a retailer reads as "the table shows the paid
  // commission instead of the earned one" (Arthur en Willemijn, 2026-09-30).
  const getCommissionRate = (commission) => {
    const bid = Number(commission.match_retailer_bid || 0);
    if (commission.commission_type === 'owed') return formatRate(bid);
    const handoffShare = Number(commission.handoff_commission_rate || 0);
    return formatRate(bid * (1 - handoffShare / 100));
  };
  const formatRate = (rate) => (Number.isInteger(rate) ? String(rate) : rate.toFixed(1));

  const TableSkeletonRow = () => (
    <TableRow>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
        <TableCell key={item}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getUserLocale()}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Commissions
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            {/* Tabs */}
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
              <Tab label="All Commissions" />
              <Tab
                label="Acquisition Spend"
                icon={<TrendingDown />}
                iconPosition="end"
              />
              <Tab
                label="Affiliate Revenue"
                icon={<TrendingUp />}
                iconPosition="end"
              />
            </Tabs>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Start Date"
                  value={filters.start_date}
                  onChange={(date) => handleFilterChange('start_date', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="End Date"
                  value={filters.end_date}
                  onChange={(date) => handleFilterChange('end_date', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small"
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink={filters.status !== undefined}>Commission Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Commission Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        }
                      },
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {Object.entries(availableStatuses).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink={filters.partner_retailer !== undefined}>Partner</InputLabel>
                  <Select
                    value={filters.partner_retailer}
                    label="Partner"
                    onChange={(e) => handleFilterChange('partner_retailer', e.target.value)}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        }
                      },
                    }}
                  >
                    <MenuItem value="">All Partners</MenuItem>
                    {partners.map((partner) => (
                      <MenuItem key={partner.retailer_id} value={partner.retailer_id}>
                        {partner.company_name} ({partner.commission_count})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                {totalCount} total commissions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={() => setExportOpen(true)}
              >
                Export
              </Button>
            </Box>

            <ExportOrdersModal
              open={exportOpen}
              onClose={() => setExportOpen(false)}
              initialFilters={filters}
            />


            {/* Table */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'order_date'}
                        direction={orderBy === 'order_date' ? order : 'asc'}
                        onClick={() => handleRequestSort('order_date')}
                      >
                        Order Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Order #</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>Commission product</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'partner_name'}
                        direction={orderBy === 'partner_name' ? order : 'asc'}
                        onClick={() => handleRequestSort('partner_name')}
                      >
                        Partner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Item price</TableCell>
                    <TableCell align="right" sx={{ minWidth: '120px', width: '120px' }}>
                      <TableSortLabel
                        active={orderBy === 'commission_amount'}
                        direction={orderBy === 'commission_amount' ? order : 'asc'}
                        onClick={() => handleRequestSort('commission_amount')}
                      >
                        Commission
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Commission Status</TableCell>
                    <TableCell align="right">No. order items</TableCell>
                    <TableCell align="right">Order value</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <>
                      <TableSkeletonRow />
                      <TableSkeletonRow />
                      <TableSkeletonRow />
                    </>
                  ) : commissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="textSecondary">
                          No commissions found for the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    commissions.map((commission) => (
                      <TableRow key={commission.id} hover>
                        <TableCell>
                          {formatDate(commission.order_timestamp)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {commission.order_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Box>
                            <Typography variant="body2" noWrap title={commission.product_name}>
                              {commission.product_name}
                            </Typography>
                            {(commission.product_color || commission.product_size) && (
                              <Typography variant="caption" color="textSecondary">
                                {[commission.product_color, commission.product_size]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {commission.partner_retailer_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={commission.commission_type === 'owed' ? 'Acq. Spend' : 'Aff. Revenue'}
                            color={commission.commission_type === 'owed' ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(commission.item_price || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: '120px', width: '120px' }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={commission.commission_type === 'owed' ? 'error.main' : 'success.main'}
                            sx={{ lineHeight: 1.2 }}
                          >
                            {formatCurrency(getCommissionAmount(commission))}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ lineHeight: 1 }}>
                            {getCommissionRate(commission)}% of item price
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Chip
                              label={statusLabels[commission.status] || commission.status}
                              color={statusColors[commission.status] || 'default'}
                              size="small"
                            />
                            {commission.invoice_id && (
                              <Chip
                                label="Invoiced"
                                size="small"
                                variant="outlined"
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/invoices/${commission.invoice_id}`);
                                }}
                              />
                            )}
                            {commission.status === 'declined' && commission.status_reason && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      Decline Reason:
                                    </Typography>
                                    <Typography variant="body2">
                                      {commission.status_reason}
                                    </Typography>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Info
                                  sx={{
                                    fontSize: 16,
                                    color: 'text.secondary',
                                    cursor: 'help'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {commission.order_item_count || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(commission.order_total_value || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/commissions/${commission.id}`, {
                                state: { filters, tabValue, page, rowsPerPage, orderBy, order }
                              })}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default CommissionsTable;